import { FC, ReactNode, MouseEvent } from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";

import CloseButtonBase from "../button/CloseButton";
import {
  StepFlowBody,
  StepFlowContent,
  StepFlowFooter,
  StepFlowHeader,
} from "../step-flow/StepFlowShell";
import {
  StepFlowAction,
  renderStepFlowAction,
} from "../step-flow/stepFlowActions";

export type ModalVariant = "dialog" | "dialog-glass" | "auth" | "fullscreen";

export type ModalSize = "auto" | "default" | "wide" | "auto-wide" | "auto-viewport";

/**
 * フッターアクション。fullscreen は StepFlow シェルと同じ型を共有する
 * （label / onClick / disabled / loading / leftIcon / rightIcon / theme / variant / autoFocus）。
 */
export type ModalAction = StepFlowAction;

export interface ModalProps {
  /** モーダルの種類（default: "dialog"） */
  variant?: ModalVariant;
  /** タイトル領域（ReactNode、未指定なら表示しない） */
  title?: ReactNode;
  /** 説明文（ReactNode、未指定なら表示しない） */
  description?: ReactNode;
  /**
   * タイトル領域のサイズバリエーション。
   * - "default"（既定）: `heading-spacing-21`（大）
   * - "medium": `heading-spacing-18`（中）。フォルダ名等の識別子用。
   * - "compact": `bodytext-13`（小）。ファイル名等のメタ情報用。
   *   CloseButton と被らないよう右側に padding を確保し、長文は折り返す。
   */
  titleSize?: "default" | "medium" | "compact";
  /**
   * Description のテキスト色（情報の重みを表現）。
   * - "default"（既定）: `--soremo-gray`。本文相当のテキスト説明。
   * - "subtle": `--soremo-light-gray`。ファイル件数・補助メタ情報など、
   *   タイトルを引き立てる軽い情報。
   */
  descriptionTone?: "default" | "subtle";
  /**
   * メインアクション（右側、推奨 theme: "primary"）。
   * 指定時は内部で Button として配置（fullscreen variant は size="large"、
   * dialog 系は size="small"）。
   * secondaryAction と組み合わせると左右両端、単独なら右寄せ。
   */
  primaryAction?: ModalAction;
  /**
   * セカンダリアクション（左側、推奨 theme: "tertiary"）。
   * primaryAction と組み合わせると左右両端配置になる。
   */
  secondaryAction?: ModalAction;
  /**
   * 自由なフッター（primary/secondary で表現不能な場合の逃げ道）。
   * 内部で sticky bottom 領域に固定配置される。
   * primaryAction / secondaryAction と併用された場合は footer が優先される。
   */
  footer?: ReactNode;
  /** fullscreen の戻るボタンハンドラ（指定時のみ左上戻るボタン表示） */
  onBack?: () => void;
  /**
   * fullscreen のヘッダー中央に Stepper 等を置くためのスロット。
   * 戻るボタンの有無に関係なく画面中央（最大 480px）に配置される。
   */
  headerExtras?: ReactNode;
  /**
   * fullscreen の本文・フッターの幅。
   * - "full"（既定）: 画面幅いっぱい（表・ボードなど広く使う画面）
   * - "reading": 中央寄せ・最大 740px（`.u-wrapper-reading` と同じ）。
   *   ステップ入力フォーム系のフローはこちらを使い、フッターのボタンも同じ幅に揃う。
   */
  bodyWidth?: "full" | "reading";
  /** クローズハンドラ（指定時のみ右上 × ボタン表示 + Overlay クリックで閉じる） */
  onClose?: () => void;
  /** dialog の縦位置（default: "center"）。auth / fullscreen では無視される */
  align?: "center" | "top";
  /**
   * dialog / dialog-glass の幅モード。
   * - "auto"（既定）: 内容にフィット（min 320px 〜 max clamp(320px,80vw,756px)）
   * - "default": 常に最大幅 clamp(320px, 80vw, 756px) で広く表示。
   *   ドラッグ＆ドロップ領域を確保したいフォーム等で使う。
   * - "wide": 常に最大幅 clamp(320px, 94vw, 1280px)。表を内包する編集フォーム等、
   *   画面幅に余裕があるだけ広く使いたい場合に使う。
   * - "auto-wide": 内容にフィットし、上限だけ wide と同じ clamp(320px, 94vw, 1280px)。
   *   画像プレビュー等、内容の縦横比にモーダルを揃えたい場合に使う。
   * - "auto-viewport": 内容にフィットし、上限はビューポート幅いっぱい
   *   calc(100vw - 32px)（縦の上限 calc(100dvh - 32px) と同じ余白）。
   *   大画面で画像を可能な限り大きく見せるプレビュー等に使う。
   */
  size?: ModalSize;
  /** メインコンテンツ */
  children?: ReactNode;
}

// dialog / dialog-glass の固定幅（size="default" / "wide"）
const FIXED_SIZE_WIDTH = {
  default: "clamp(320px, 80vw, 756px)",
  wide: "clamp(320px, 94vw, 1280px)",
} as const;

// dialog / dialog-glass の auto 系 max-width（内容にフィットし上限だけ変わる）
const AUTO_SIZE_MAX_WIDTH = {
  auto: "clamp(320px, 80vw, 756px)",
  "auto-wide": "clamp(320px, 94vw, 1280px)",
  "auto-viewport": "calc(100vw - 32px)",
} as const;

// variant ごとの背景・blur 値（Footer の背景にも共通利用）
const VARIANT_BACKGROUND: Record<ModalVariant, string> = {
  dialog: "rgba(255, 255, 255, 1)",
  "dialog-glass": "rgba(255, 255, 255, 0.16)",
  auth: "rgba(255, 255, 255, 0.16)",
  fullscreen: "rgba(255, 255, 255, 1)",
};

const VARIANT_BACKDROP_FILTER: Record<ModalVariant, string> = {
  dialog: "none",
  "dialog-glass": "blur(13px)",
  auth: "blur(4px)",
  fullscreen: "none",
};

// ===== Styled Components: dialog / dialog-glass / auth 共通の Overlay =====

const Overlay = styled.div<{ $variant: ModalVariant }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ $variant }) =>
    $variant === "auth" ? "transparent" : "rgba(0, 0, 0, 0.05)"};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

// ===== Styled Components: dialog / dialog-glass / auth の Content =====

const Content = styled.div<{
  $variant: ModalVariant;
  $align: "center" | "top";
  $size: ModalSize;
}>`
  position: fixed;
  max-height: calc(100dvh - 32px);
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  z-index: 1001;

  display: flex;
  flex-direction: column;

  animation-name: fadeIn;
  animation-fill-mode: forwards;
  animation-duration: 300ms;
  animation-timing-function: ease-out;

  background: ${({ $variant }) => VARIANT_BACKGROUND[$variant]};

  ${({ $variant }) => {
    if ($variant === "dialog-glass" || $variant === "auth") {
      const filter = VARIANT_BACKDROP_FILTER[$variant];
      return css`
        backdrop-filter: ${filter};
        -webkit-backdrop-filter: ${filter};
        box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
      `;
    }
    return css`
      box-shadow: 2px 4px 8px 3px rgba(0, 0, 0, 0.1);
    `;
  }}

  ${({ $variant, $align, $size }) => {
    if ($variant === "auth") {
      // 黄金比 38.2% 位置 + 360px 幅
      return css`
        width: clamp(320px, 38.2vw, 360px);
        top: 38.2%;
        left: 50%;
        transform: translate(-50%, -50%);
      `;
    }

    // dialog / dialog-glass: size="default" / "wide" は常に最大幅で広く表示、
    // auto 系は内容にフィットし max-width の上限だけが変わる
    const widthDecl =
      $size === "default" || $size === "wide"
        ? css`
            width: ${FIXED_SIZE_WIDTH[$size]};
          `
        : css`
            width: auto;
            min-width: min(320px, calc(100vw - 32px));
            max-width: ${AUTO_SIZE_MAX_WIDTH[$size]};
          `;
    if ($align === "top") {
      return css`
        ${widthDecl}
        top: 16px;
        left: 50%;
        transform: translateX(-50%);
      `;
    }
    return css`
      ${widthDecl}
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    `;
  }}
`;

// dialog / dialog-glass / auth の本体（スクロール領域）。
// 左右は 12px に統一し、上下は title / footer の有無でコンパクトに：
//   - $hasTitle: padding-top を 0 に（Header 側で 24/12/16 を確保しているため）
//   - $hasFooter: padding-bottom を 12px に（フッターとの間隔を詰める）
const Body = styled.div<{
  $variant: ModalVariant;
  $hasTitle: boolean;
  $hasFooter: boolean;
}>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ $variant, $hasTitle, $hasFooter }) => {
    if ($variant === "auth") return "32px 12px";
    const top = $hasTitle ? "0" : "24px";
    const bottom = $hasFooter ? "12px" : "24px";
    return `${top} 12px ${bottom}`;
  }};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
`;

// ===== Styled Components: fullscreen の Overlay = Content =====

const FullScreenOverlay = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  background-color: ${VARIANT_BACKGROUND.fullscreen};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  animation-name: fadeIn;
  animation-fill-mode: forwards;
  animation-duration: 300ms;
  animation-timing-function: ease-out;
`;

// fullscreen のヘッダー / 本文 / フッターは StepFlowShell の部品を使う
// （ページ型の StepFlowLayout と同じ見え方にするため）。

// ===== Styled Components: 共通の要素 =====

// 共通 CloseButton を position だけ追加してラップ
const CloseButton = styled(CloseButtonBase)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`;

// title と description を Body の外に出す Header。flex-shrink: 0 で常に
// モーダル上部に留まる（Body のスクロールに引きずられない）。
// - default / medium: 12 / 12 / 16（top / sides / bottom）。bottom 16px で
//   見出し風タイトルから本文への自然な余白を作る。
// - compact: 12 / 12 / 8。タイトルが軽量メタ表示なので、本文への距離も
//   詰めてヘッダー全体を引き締める。
const DialogHeader = styled.div<{ $titleSize: TitleSize }>`
  flex-shrink: 0;
  padding: ${({ $titleSize }) =>
    $titleSize === "compact" ? "12px 12px 8px" : "12px 12px 16px"};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// タイトルは titleSize で 3 段階（default / medium / compact）。
// compact では CloseButton (top:8px / right:8px / ≒36×32) と被らないよう
// right padding を確保し、長いファイル名は折り返す。
type TitleSize = "default" | "medium" | "compact";
type DescriptionTone = "default" | "subtle";

const TitleArea = styled.div<{ $titleSize: TitleSize }>`
  width: 100%;
  text-align: left;
  ${({ $titleSize }) =>
    $titleSize === "compact"
      ? css`
          padding-right: 36px;
          word-break: break-all;
        `
      : ""}
`;

const Description = styled.div<{ $tone: DescriptionTone }>`
  width: 100%;
  text-align: left;
  white-space: pre-wrap;
  color: ${({ $tone }) =>
    $tone === "subtle"
      ? "var(--soremo-light-gray)"
      : "var(--soremo-gray)"};
`;

const TITLE_CLASS: Record<TitleSize, string> = {
  default: "heading-spacing-21",
  medium: "heading-spacing-18",
  compact: "bodytext-13",
};

// dialog / dialog-glass / auth の Footer
// Body の外、Content の flex 直下に置く独立要素なので
// 負 margin / sticky は不要。flex-shrink: 0 で常時表示される。
// 左右 padding は Body と同じ 12px で揃える。
const DialogFooter = styled.div<{
  $singleAction: boolean;
}>`
  flex-shrink: 0;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: ${({ $singleAction }) =>
    $singleAction ? "flex-end" : "space-between"};
  gap: 8px;
  border-top: 1px solid var(--soremo-border);
`;

// ===== Helpers =====

// dialog 系は領域が限られるため small。fullscreen は StepFlowFooter 側で large。
// autoFocus が当たるアクションは primary + filled に強調される（renderStepFlowAction）。
const renderDialogActions = (
  primary?: ModalAction,
  secondary?: ModalAction,
): ReactNode => (
  <>
    {secondary && renderStepFlowAction(secondary, "tertiary", "filled", "small")}
    {primary && renderStepFlowAction(primary, "primary", "filled", "small")}
  </>
);

// ===== Component =====

const stopPropagation = (e: MouseEvent) => e.stopPropagation();

/**
 * 共通モーダル。
 *
 * - variant: "dialog"（既定）/ "dialog-glass" / "auth" / "fullscreen"
 * - フッターは primaryAction / secondaryAction で構造化、または footer slot で自由配置
 * - フッターは全 variant で sticky 固定（スクロール時も常に画面下に表示）
 * - 2 アクションは左右両端、1 アクションのみは右寄せ
 * - 削除・退出などの「実行前確認」には本コンポーネントを直接使わず、
 *   統一デザインの `ConfirmModal`（同ディレクトリ）を使うこと。
 *
 * document.body にポータルすることで、Preview 等の全画面オーバーレイ
 * (createPortal → body, z-index: 999) より確実に上に表示する。
 */
const Modal: FC<ModalProps> = ({
  variant: variantProp,
  title,
  description,
  titleSize = "default",
  descriptionTone = "default",
  primaryAction,
  secondaryAction,
  footer,
  onBack,
  headerExtras,
  bodyWidth = "full",
  onClose,
  align = "center",
  size = "auto",
  children,
}) => {
  const variant: ModalVariant = variantProp ?? "dialog";

  const hasActions = Boolean(primaryAction || secondaryAction);
  const hasFooter = Boolean(footer) || hasActions;
  const singleAction = Boolean(
    !footer && primaryAction && !secondaryAction,
  );

  const footerContent =
    footer ?? renderDialogActions(primaryAction, secondaryAction);

  // ----- fullscreen -----
  if (variant === "fullscreen") {
    const body = (
      <>
        {description && (
          <Description
            $tone={descriptionTone}
            className="bodytext-13 u-line-height-150"
          >
            {description}
          </Description>
        )}
        {children}
      </>
    );
    return createPortal(
      <FullScreenOverlay role="dialog" aria-modal="true">
        {(onBack || headerExtras || title) && (
          <StepFlowHeader
            onBack={onBack}
            title={
              title ? (
                <TitleArea
                  $titleSize={titleSize}
                  className={TITLE_CLASS[titleSize]}
                >
                  {title}
                </TitleArea>
              ) : undefined
            }
          >
            {headerExtras}
          </StepFlowHeader>
        )}
        <StepFlowBody className="bodytext">
          {bodyWidth === "reading" ? (
            <StepFlowContent>{body}</StepFlowContent>
          ) : (
            body
          )}
        </StepFlowBody>
        {hasFooter && (
          <StepFlowFooter
            footer={footer}
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
            width={bodyWidth === "full" ? "full" : "content"}
          />
        )}
      </FullScreenOverlay>,
      document.body,
    );
  }

  // ----- dialog / dialog-glass / auth -----
  return createPortal(
    <Overlay $variant={variant} onClick={onClose}>
      <Content
        role="dialog"
        aria-modal="true"
        onClick={stopPropagation}
        $variant={variant}
        $align={align}
        $size={size}
      >
        {onClose && <CloseButton onClick={onClose} />}
        {(title || description) && (
          <DialogHeader $titleSize={titleSize}>
            {title && (
              <TitleArea
                $titleSize={titleSize}
                className={TITLE_CLASS[titleSize]}
              >
                {title}
              </TitleArea>
            )}
            {description && (
              <Description
                $tone={descriptionTone}
                className="bodytext-13 u-line-height-150"
              >
                {description}
              </Description>
            )}
          </DialogHeader>
        )}
        <Body
          className="bodytext"
          $variant={variant}
          $hasTitle={Boolean(title || description)}
          $hasFooter={hasFooter}
        >
          {children}
        </Body>
        {hasFooter && (
          <DialogFooter $singleAction={singleAction}>
            {footerContent}
          </DialogFooter>
        )}
      </Content>
    </Overlay>,
    document.body,
  );
};

export default Modal;
